import { useEffect } from 'react';

const FOCUSABLE_SELECTOR = 'a[href],button,input,select,textarea,[tabindex]:not([tabindex="-1"])';

const getFocusableElements = (root: ParentNode) =>
  Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
    (element) => !element.hasAttribute('disabled') && element.getAttribute('aria-hidden') !== 'true'
  );

const getNextAfterTrigger = (popup: HTMLElement, trigger: HTMLElement | null) => {
  const documentFocusables = getFocusableElements(document).filter((element) => !popup.contains(element));
  const triggerIndex = trigger ? documentFocusables.indexOf(trigger) : -1;
  return triggerIndex === -1 ? null : documentFocusables[triggerIndex + 1];
};

// Popper with auto mode does not work when triggered from main
// In manual mode we have to take care about focus trap ourselves
export const usePopperFocusTrap = (
  isOpen: boolean,
  triggerRef: React.RefObject<HTMLElement | null>,
  popupRef: React.RefObject<HTMLElement | null>,
  close: () => void,
  triggerId?: string
) => {
  const focusFirstInPopup = () => {
    const popup = popupRef.current;
    if (!popup) {
      return;
    }

    const firstFocusable = getFocusableElements(popup)[0];
    firstFocusable?.focus();
  };

  const handlePopupKeyDown = (event: React.KeyboardEvent) => {
    if (!isOpen) {
      return;
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      close();
      triggerRef.current?.focus();
      return;
    }

    if (event.key !== 'Tab') {
      return;
    }

    const popup = popupRef.current;
    if (!popup) {
      return;
    }

    const popupFocusables = getFocusableElements(popup);
    if (popupFocusables.length === 0) {
      return;
    }

    const first = popupFocusables[0];
    const last = popupFocusables[popupFocusables.length - 1];
    const active = document.activeElement;

    if (event.shiftKey && active === first) {
      event.preventDefault();
      triggerRef.current?.focus();
      return;
    }

    if (!event.shiftKey && active === last) {
      event.preventDefault();
      (getNextAfterTrigger(popup, triggerRef.current) ?? triggerRef.current)?.focus();
    }
  };

  useEffect(() => {
    const trigger =
      triggerRef.current ?? (triggerId ? document.querySelector<HTMLElement>(`[id="${triggerId}"]`) : null);
    if (!trigger) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (!isOpen) {
        return;
      }

      if (event.key === 'Escape') {
        event.preventDefault();
        close();
        trigger.focus();
        return;
      }

      if (event.key !== 'Tab' || event.shiftKey) {
        return;
      }

      event.preventDefault();
      focusFirstInPopup();
    };

    trigger.addEventListener('keydown', handleKeyDown);
    return () => {
      trigger.removeEventListener('keydown', handleKeyDown);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [close, isOpen, triggerId, triggerRef]);

  return { handlePopupKeyDown };
};
